/**
 * 보안 파일 저장소 서비스
 * 암호화된 파일 업로드/다운로드 및 PIN 보호 기능
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  limit
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  getMetadata,
  getBytes
} from 'firebase/storage';
import { db, storage, auth } from './firebase';
import { encryptData, decryptData } from './webCryptoService';
import { verifyProtection } from './unifiedPinService';

const COLLECTION_NAME = 'secureFiles';
const STORAGE_PATH = 'secure-files';
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'text/plain',
  'application/json',
  'application/zip',
  'application/x-zip-compressed',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
];

/**
 * 파일 메타데이터 인터페이스
 */
export const FileMetadata = {
  id: '',
  userId: '',
  fileName: '',
  originalName: '',
  fileSize: 0,
  mimeType: '',
  storagePath: '',
  encryptedUrl: '',
  isPinProtected: false,
  pinHash: null,
  pinSalt: null,
  category: 'general',
  tags: [],
  description: '',
  isShared: false,
  shareExpiresAt: null,
  createdAt: null,
  updatedAt: null,
  lastAccessedAt: null,
  accessCount: 0
};

/**
 * PIN 검증 헬퍼 함수 - Master PIN만 사용
 */
async function verifyPin(pin, hash, salt) {
  try {
    const user = auth.currentUser;
    if (!user) return false;
    
    // 6자리 PIN 검증 (Master PIN만 사용)
    if (pin.length === 6) {
      // Master PIN으로 검증
      return await verifyProtection(user.uid, pin);
    }
    
    return false;
  } catch (error) {
    console.error('PIN verification error:', error);
    return false;
  }
}

/**
 * 파일 유효성 검사
 */
function validateFile(file) {
  if (!file) {
    throw new Error('파일이 선택되지 않았습니다.');
  }

  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`파일 크기는 ${MAX_FILE_SIZE / (1024 * 1024)}MB 이하여야 합니다.`);
  }

  if (!ALLOWED_FILE_TYPES.includes(file.type) && !file.type.startsWith('image/')) {
    throw new Error('지원하지 않는 파일 형식입니다.');
  }

  return true;
}

/**
 * 파일을 암호화하여 업로드
 */
export async function uploadSecureFile(file, metadata = {}) {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('인증이 필요합니다.');

    // 파일 유효성 검사
    validateFile(file);

    // 파일을 ArrayBuffer로 읽기
    const fileBuffer = await file.arrayBuffer();
    const fileData = new Uint8Array(fileBuffer);

    // 파일 내용을 Base64로 변환 (큰 파일도 처리 가능하도록 청크로 처리)
    let base64String = '';
    const chunkSize = 8192; // 8KB chunks to avoid stack overflow
    for (let i = 0; i < fileData.length; i += chunkSize) {
      const chunk = fileData.slice(i, i + chunkSize);
      base64String += String.fromCharCode.apply(null, chunk);
    }
    base64String = btoa(base64String);

    // 파일 내용 암호화
    const encryptedData = await encryptData(base64String, user.uid);

    // 암호화된 데이터를 JSON으로 래핑 (문자열 안전성 보장)
    const encryptedJson = JSON.stringify({ data: encryptedData });
    
    // JSON을 Blob으로 변환
    const encryptedBlob = new Blob([encryptedJson], { type: 'application/json' });

    // Storage 경로 생성
    const timestamp = Date.now();
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const storagePath = `${STORAGE_PATH}/${user.uid}/${timestamp}_${sanitizedFileName}`;
    const storageRef = ref(storage, storagePath);

    // Firebase Storage에 업로드
    const uploadResult = await uploadBytes(storageRef, encryptedBlob, {
      contentType: 'application/json',
      customMetadata: {
        originalName: file.name,
        originalType: file.type,
        encrypted: 'true',
        version: '2' // 새로운 JSON 형식 버전
      }
    });

    // 다운로드 URL 가져오기
    const downloadUrl = await getDownloadURL(uploadResult.ref);

    // Firestore에 메타데이터 저장
    const fileDoc = {
      userId: user.uid,
      fileName: sanitizedFileName,
      originalName: file.name,
      fileSize: file.size,
      mimeType: file.type,
      storagePath,
      encryptedUrl: downloadUrl,
      isPinProtected: metadata.enablePin || false,
      pinHash: null, // Master PIN 시스템에서는 개별 PIN 해시 저장하지 않음
      pinSalt: null,
      category: metadata.category || 'general',
      tags: metadata.tags || [],
      description: metadata.description || '',
      isShared: false,
      shareExpiresAt: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastAccessedAt: null,
      accessCount: 0
    };

    const docRef = doc(collection(db, COLLECTION_NAME));
    await setDoc(docRef, fileDoc);

    return {
      id: docRef.id,
      ...fileDoc,
      createdAt: new Date()
    };
  } catch (error) {
    console.error('Error uploading secure file:', error);
    throw error;
  }
}

/**
 * 암호화된 파일 다운로드 및 복호화
 */
export async function downloadSecureFile(fileId, pin = null) {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('인증이 필요합니다.');

    // 파일 메타데이터 가져오기
    const fileDoc = await getDoc(doc(db, COLLECTION_NAME, fileId));
    if (!fileDoc.exists()) {
      throw new Error('파일을 찾을 수 없습니다.');
    }

    const fileData = fileDoc.data();
    
    // 권한 확인
    if (fileData.userId !== user.uid) {
      throw new Error('파일에 대한 접근 권한이 없습니다.');
    }

    // PIN 검증 (Master PIN 사용)
    if (fileData.isPinProtected) {
      if (!pin) {
        throw new Error('PIN이 필요합니다.');
      }
      // Master PIN 검증 (verifyProtection 함수 사용)
      const isValidPin = await verifyProtection(user.uid, pin);
      if (!isValidPin) {
        throw new Error('잘못된 PIN입니다.');
      }
    }

    // Storage에서 암호화된 파일 다운로드 (Firebase SDK 사용)
    const storageRef = ref(storage, fileData.storagePath);
    const encryptedBytes = await getBytes(storageRef);
    
    // JSON 파싱하여 암호화된 데이터 추출
    const jsonString = new TextDecoder().decode(encryptedBytes);
    const parsed = JSON.parse(jsonString);
    const encryptedData = parsed.data;

    // 파일 복호화
    const decryptedBase64 = await decryptData(encryptedData, user.uid);
    
    // Base64를 바이너리로 변환
    const binaryString = atob(decryptedBase64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Blob 생성
    const blob = new Blob([bytes], { type: fileData.mimeType });

    // 접근 기록 업데이트
    await updateDoc(doc(db, COLLECTION_NAME, fileId), {
      lastAccessedAt: serverTimestamp(),
      accessCount: (fileData.accessCount || 0) + 1
    });

    return {
      blob,
      fileName: fileData.originalName,
      mimeType: fileData.mimeType
    };
  } catch (error) {
    // PIN 검증 실패는 사용자 입력 오류이므로 에러 로그 생략
    if (!error.message?.includes('PIN')) {
      console.error('Error downloading secure file:', error);
    }
    throw error;
  }
}

/**
 * 사용자의 보안 파일 목록 가져오기
 */
export async function getSecureFiles(filters = {}) {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('인증이 필요합니다.');

    // 기본 쿼리 - userId만으로 필터링
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    let files = [];
    
    snapshot.forEach((doc) => {
      files.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // 클라이언트 측 필터링
    if (filters.category) {
      files = files.filter(file => file.category === filters.category);
    }

    if (filters.pinProtectedOnly) {
      files = files.filter(file => file.isPinProtected === true);
    }

    return files;
  } catch (error) {
    console.error('Error getting secure files:', error);
    throw error;
  }
}

/**
 * 파일 메타데이터 업데이트
 */
export async function updateFileMetadata(fileId, updates) {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('인증이 필요합니다.');

    // 권한 확인
    const fileDoc = await getDoc(doc(db, COLLECTION_NAME, fileId));
    if (!fileDoc.exists()) {
      throw new Error('파일을 찾을 수 없습니다.');
    }
    
    if (fileDoc.data().userId !== user.uid) {
      throw new Error('파일 수정 권한이 없습니다.');
    }

    // PIN 보호 상태 업데이트 처리
    // isPinProtected 플래그만 업데이트 (Master PIN 사용)
    if (updates.isPinProtected !== undefined) {
      // Master PIN 시스템에서는 개별 PIN 해시를 저장하지 않음
      if (!updates.isPinProtected) {
        // PIN 보호를 제거하는 경우 관련 필드 정리
        updates.pinHash = null;
        updates.pinSalt = null;
      }
    }

    await updateDoc(doc(db, COLLECTION_NAME, fileId), {
      ...updates,
      updatedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error('Error updating file metadata:', error);
    throw error;
  }
}

/**
 * 보안 파일 삭제
 */
export async function deleteSecureFile(fileId, pin = null) {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('인증이 필요합니다.');

    // 파일 메타데이터 가져오기
    const fileDoc = await getDoc(doc(db, COLLECTION_NAME, fileId));
    if (!fileDoc.exists()) {
      throw new Error('파일을 찾을 수 없습니다.');
    }

    const fileData = fileDoc.data();
    
    // 권한 확인
    if (fileData.userId !== user.uid) {
      throw new Error('파일 삭제 권한이 없습니다.');
    }

    // PIN 검증 (Master PIN 사용)
    if (fileData.isPinProtected) {
      if (!pin) {
        throw new Error('PIN이 필요합니다.');
      }
      // Master PIN 검증 (verifyProtection 함수 사용)
      const isValidPin = await verifyProtection(user.uid, pin);
      if (!isValidPin) {
        throw new Error('잘못된 PIN입니다.');
      }
    }

    // Storage에서 파일 삭제
    const storageRef = ref(storage, fileData.storagePath);
    await deleteObject(storageRef);

    // Firestore에서 메타데이터 삭제
    await deleteDoc(doc(db, COLLECTION_NAME, fileId));

    return true;
  } catch (error) {
    console.error('Error deleting secure file:', error);
    throw error;
  }
}

/**
 * 파일 공유 링크 생성
 */
export async function createShareLink(fileId, expirationHours = 24, pin = null) {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('인증이 필요합니다.');

    // 파일 확인 및 권한 검증
    const fileDoc = await getDoc(doc(db, COLLECTION_NAME, fileId));
    if (!fileDoc.exists()) {
      throw new Error('파일을 찾을 수 없습니다.');
    }
    
    const fileData = fileDoc.data();
    if (fileData.userId !== user.uid) {
      throw new Error('파일 공유 권한이 없습니다.');
    }

    // PIN 검증 (Master PIN 사용)
    if (fileData.isPinProtected && pin) {
      const isValidPin = await verifyProtection(user.uid, pin);
      if (!isValidPin) {
        throw new Error('잘못된 PIN입니다.');
      }
    }

    // 만료 시간 설정
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + expirationHours);

    // 공유 정보 업데이트
    await updateDoc(doc(db, COLLECTION_NAME, fileId), {
      isShared: true,
      shareExpiresAt: expiresAt,
      updatedAt: serverTimestamp()
    });

    // 공유 링크 생성 (실제 구현에서는 토큰 기반 링크 생성)
    const shareLink = `${window.location.origin}/shared-file/${fileId}`;

    return {
      link: shareLink,
      expiresAt
    };
  } catch (error) {
    console.error('Error creating share link:', error);
    throw error;
  }
}

export default {
  uploadSecureFile,
  downloadSecureFile,
  getSecureFiles,
  updateFileMetadata,
  deleteSecureFile,
  createShareLink
};